import React, { memo, useMemo } from 'react';
import { NavItem } from './types';
import { cn } from '../../../utils';
import { Tooltip, TooltipTrigger } from '../../ui/tooltip';
import { SidebarTooltipContent } from './sidebar-tooltip-content';

import {
  ANIMATION_DURATION,
  ACTIVE_ICON_FILTER,
  INACTIVE_ICON_FILTER,
  HIGHLIGHT_ICON_FILTER,
} from './constants';
import { renderNavIcon } from './utils/render-icon';
import { SidebarLinkWithTooltip } from './sidebar-link-with-tooltip';
import { getNavItemId } from './utils/get-nav-item-id';

interface AppSidebarItemProps {
  item: NavItem;
  isActive: boolean;
  isCollapsed: boolean;
  className?: string;
  onNavigate?: (href: string, isExternal: boolean) => void;
  isExtended?: boolean;
}

function AppSidebarItemComponent({
  item,
  isActive,
  isCollapsed,
  className,
  onNavigate,
  isExtended = false,
}: AppSidebarItemProps) {
  const isHighlight = item.variant === 'highlight';

  const iconFilter = useMemo(() => {
    if (isHighlight) return HIGHLIGHT_ICON_FILTER;
    return isActive ? ACTIVE_ICON_FILTER : INACTIVE_ICON_FILTER;
  }, [isActive, isHighlight]);

  const renderIcon = () => {
    if (!item.icon) return null;

    if (typeof item.icon === 'string') {
      return (
        <img
          src={item.icon}
          alt={item.label}
          className={cn(
            'tw-h-5 tw-w-5 tw-flex-shrink-0 tw-object-contain tw-transition-all tw-duration-200',
            !isActive && 'group-hover:tw-opacity-100'
          )}
          style={{
            width: '20px',
            height: '20px',
            minWidth: '20px',
            minHeight: '20px',
            filter: iconFilter,
          }}
        />
      );
    }

    const iconClassName = 'tw-h-5 tw-w-5 tw-flex-shrink-0';
    const iconColor = isHighlight || isActive ? 'rgb(0 70 160 / 1)' : '#1f1f1f';

    return renderNavIcon(item.icon, iconClassName, iconColor);
  };

  const labelStyle = useMemo(
    () => ({
      marginLeft: isCollapsed ? '0' : '12px',
      maxWidth: isCollapsed ? '0' : '100%',
      opacity: isCollapsed ? 0 : 1,
      visibility: isCollapsed ? ('hidden' as const) : ('visible' as const),
      lineHeight: 1,
      transition: `margin-left ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1), max-width ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1), opacity ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1), visibility ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1), color ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`,
      overflow: 'hidden' as const,
      ...(isHighlight && {
        color: 'rgb(0 70 160 / 1)',
      }),
      ...(!isHighlight &&
        isActive && {
          color: 'rgb(0 70 160 / 1)',
        }),
      ...(!isHighlight &&
        !isActive && {
          color: '#1f1f1f',
        }),
    }),
    [isCollapsed, isActive, isHighlight]
  );

  const linkContent = (
    <>
      <span
        className={cn(
          'tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center [&_svg]:tw-text-current [&_img]:tw-transition-all'
        )}
        style={{
          width: '20px',
          height: '20px',
          minWidth: '20px',
          minHeight: '20px',
          color: isHighlight || isActive ? 'rgb(0 70 160 / 1)' : '#1f1f1f',
        }}
      >
        {renderIcon()}
      </span>
      <span className="tw-truncate tw-whitespace-nowrap" style={labelStyle}>
        {item.label}
      </span>
    </>
  );

  const baseClasses = cn(
    'tw-group tw-flex tw-items-center tw-rounded-md tw-overflow-hidden tw-relative tw-min-w-0 tw-no-underline',
    'tw-font-medium',
    isHighlight && 'tw-text-[#0046a0] hover:tw-bg-[#0046a0]/5',
    !isHighlight &&
      isActive &&
      'tw-bg-[#0046a0]/10 tw-text-[#0046a0] tw-shadow-sm',
    !isHighlight && !isActive && 'hover:tw-bg-gray-50',
    item.onClick && 'tw-cursor-pointer',
    className
  );

  const linkStyle = useMemo(
    () => ({
      paddingTop: isCollapsed ? '6px' : '8px',
      paddingBottom: isCollapsed ? '6px' : '8px',
      paddingLeft: '12px',
      paddingRight: '12px',
      minHeight: '40px',
      lineHeight: 1,
      margin: 0,
      boxSizing: 'border-box' as const,
      fontSize: '14px',
      fontWeight: 500,
      transition: `padding-top ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1), padding-bottom ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1), color ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`,
      ...(isHighlight && {
        color: 'rgb(0 70 160 / 1)',
      }),
      ...(!isHighlight &&
        isActive && {
          backgroundColor: 'rgb(0 70 160 / 0.1)',
          color: 'rgb(0 70 160 / 1)',
        }),
      ...(!isHighlight &&
        !isActive && {
          color: '#1f1f1f',
        }),
    }),
    [isActive, isHighlight, isCollapsed]
  );

  const tooltipContent = useMemo(() => {
    if (isCollapsed) {
      return (
        <div className="tw-space-y-1.5 tw-py-0.5">
          <div className="tw-font-semibold tw-text-sm tw-leading-tight">
            {item.label}
          </div>
          {item.description && (
            <div className="tw-text-xs tw-text-gray-300/90 tw-leading-relaxed tw-max-w-[200px]">
              {item.description}
            </div>
          )}
        </div>
      );
    }

    if (!item.description) return null;

    return (
      <div className="tw-text-xs tw-text-gray-200 tw-leading-relaxed tw-py-0.5 tw-max-w-[200px]">
        {item.description}
      </div>
    );
  }, [isCollapsed, item.label, item.description]);

  const shouldShowTooltip = isCollapsed || !!item.description;

  const handleClick = (
    e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>
  ) => {
    if (item.onClick) {
      e.preventDefault();
      item.onClick();
      return;
    }
    if (onNavigate) {
      e.preventDefault();
      onNavigate(item.href, item.isExternal || false);
    }
  };

  const itemId = useMemo(() => getNavItemId(item.label, isExtended), [
    item.label,
    isExtended,
  ]);

  const renderClickableButton = () => (
    <button
      type="button"
      id={itemId}
      onClick={handleClick}
      className={cn(
        baseClasses,
        'tw-no-underline tw-border-0 tw-bg-transparent tw-w-full tw-text-left'
      )}
      style={linkStyle}
    >
      {linkContent}
    </button>
  );

  // linkProps removed - using className and style directly on links

  // If onClick is provided, render a button instead of a link
  if (item.onClick) {
    if (shouldShowTooltip && tooltipContent) {
      return (
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>{renderClickableButton()}</TooltipTrigger>
          <SidebarTooltipContent
            side="right"
            sideOffset={15}
            className="tw-bg-gray-900/95 tw-text-white tw-border tw-border-gray-700/50 tw-shadow-lg tw-backdrop-blur-sm tw-px-3 tw-py-2.5 tw-rounded-md"
          >
            {tooltipContent}
          </SidebarTooltipContent>
        </Tooltip>
      );
    }
    return renderClickableButton();
  }

  if (shouldShowTooltip && tooltipContent) {
    if (item.isExternal) {
      return (
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <a
              id={itemId}
              href={item.href}
              className={cn(baseClasses, 'tw-no-underline')}
              style={linkStyle}
              onClick={handleClick}
            >
              {linkContent}
            </a>
          </TooltipTrigger>
          <SidebarTooltipContent
            side="right"
            sideOffset={15}
            className="tw-bg-gray-900/95 tw-text-white tw-border tw-border-gray-700/50 tw-shadow-lg tw-backdrop-blur-sm tw-px-3 tw-py-2.5 tw-rounded-md"
          >
            {tooltipContent}
          </SidebarTooltipContent>
        </Tooltip>
      );
    }

    return (
      <SidebarLinkWithTooltip
        id={itemId}
        href={item.href}
        tooltipContent={tooltipContent}
        className={cn(baseClasses, 'tw-no-underline')}
        style={linkStyle}
        onClick={handleClick}
      >
        {linkContent}
      </SidebarLinkWithTooltip>
    );
  }

  return (
    <a
      id={itemId}
      href={item.href}
      className={cn(baseClasses, 'tw-no-underline')}
      style={linkStyle}
      onClick={handleClick}
    >
      {linkContent}
    </a>
  );
}

export const AppSidebarItem = memo(
  AppSidebarItemComponent,
  (prevProps, nextProps) => {
    return (
      prevProps.item.href === nextProps.item.href &&
      prevProps.item.description === nextProps.item.description &&
      prevProps.item.onClick === nextProps.item.onClick &&
      prevProps.item.variant === nextProps.item.variant &&
      prevProps.isActive === nextProps.isActive &&
      prevProps.isCollapsed === nextProps.isCollapsed &&
      prevProps.className === nextProps.className
    );
  }
);
